import React from "react";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  FileText,
  Calculator,
  TrendingUp,
  ClipboardList,
  DollarSign,
  Target,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function BidsManagementPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Bid & Proposal Management for Cleaning Companies"
        description="Create professional bids with accurate pricing. Pricing calculator, workloading estimates, and proposal generation for cleaning contractors."
        keywords={[
          "bid management software",
          "cleaning proposal software",
          "janitorial estimating software",
          "commercial cleaning bids",
        ]}
        url="/features/bids-management"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Features", url: "/features/bids-management" },
            { name: "Bid & Proposal Management", url: "/features/bids-management" },
          ]),
          schemas.createFeatureModuleSchema({
            name: "Bid & Proposal Management",
            description:
              "Create professional bids with accurate pricing. Pricing calculator, workloading estimates, and proposal generation for cleaning contractors.",
            url: "/features/bids-management",
            featureList: [
              "Precision Square-Footage Pricing",
              "ISSA Standard Workloading Calculations",
              "AI-Generated Cleaning Proposals",
              "Dynamic Scope of Work Matrices",
              "Digital Client Approval & E-Signatures",
            ],
          }),
        ]}
      />
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-20 pt-32">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-12 lg:flex-row">
            <div className="space-y-8 lg:w-1/2">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600">
                <FileText className="h-4 w-4" />
                <span>Bid & Proposal Management</span>
              </div>

              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
                Win More{" "}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Contracts
                </span>
              </h1>

              <p className="max-w-xl text-lg leading-relaxed text-slate-600">
                Create professional bids with accurate pricing, detailed scope of work, and
                compelling proposals. Track every opportunity from lead to contract.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/sign-up">
                  <Button
                    size="lg"
                    className="h-12 rounded-full bg-blue-600 px-8 text-lg hover:bg-blue-700"
                  >
                    Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative lg:w-1/2">
              <div className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-slate-200/50 bg-white/50 shadow-2xl backdrop-blur-sm">
                <img
                  src="/assets/feature_bid_management.png"
                  alt="Bids Management Dashboard"
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-12 -right-12 -z-10 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Deep Dive */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900">
              Complete Bid Lifecycle Management
            </h2>
            <p className="text-slate-600">
              From initial estimate to signed contract. Build professional proposals that showcase
              your expertise and win more business.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                <Calculator className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Pricing Calculator</h3>
              <p className="mb-4 text-slate-600">
                Calculate accurate pricing with our intelligent cost estimator. Factor in labor,
                supplies, equipment, and overhead to ensure profitable bids.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Cost Breakdown Analysis
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Profit Margin Optimizer
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100">
                <ClipboardList className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Workloading Calculator</h3>
              <p className="mb-4 text-slate-600">
                Calculate labor hours and staffing needs for any facility. Determine the right team
                size based on square footage, cleaning frequencies, and production rates.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Labor Hour Estimates
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Staffing Recommendations
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm"
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-slate-900">Proposal Generation</h3>
              <p className="mb-4 text-slate-600">
                Generate professional PDF proposals with your branding. Present your services with
                compelling documents that close deals.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> Custom Branding
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <Check className="mr-2 h-4 w-4 text-green-500" /> One-Click PDF Export
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 p-12">
            <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center text-center">
              <h2 className="mb-6 text-3xl font-bold text-white">AI-Powered Bid Assistance</h2>
              <p className="mb-8 text-lg text-slate-300">
                Let our AI help you create winning proposals. Ask questions like "What should I
                charge for a 50,000 sq ft office building?" or "Draft a scope of work for daily
                janitorial services."
              </p>
              <Link to="/sign-up">
                <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                  Get Started Now
                </Button>
              </Link>
            </div>
            {/* Background Abstract */}
            <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_50%_120%,rgba(59,130,246,0.2),rgba(0,0,0,0))]" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
